import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import LinearProgress from "@mui/joy/LinearProgress";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import { styled } from "@mui/joy/styles";
import React from "react";
import { Task, TaskId } from "../../../models/task";

export interface UploadTask extends Task {
  file?: File;
}

export interface DocumentUploadStatusProps {
  uploads: UploadTask[];
  onRemoveCompleted: (taskId: TaskId) => void;
  onClose: () => void;
}

const StatusContainer = styled(Card)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(2),
  left: theme.spacing(2),
  width: "400px",
  maxHeight: "60vh",
  overflow: "hidden",
  zIndex: 1000,
  boxShadow: theme.shadow.lg,
}));

const UploadListContainer = styled(Box)({
  maxHeight: "300px",
  overflowY: "auto",
});

export const DocumentUploadStatus: React.FC<DocumentUploadStatusProps> = ({
  uploads,
  onRemoveCompleted,
  onClose,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  const pendingUploads = uploads.filter(
    (upload) => upload.status === "requested" || upload.status === "in-progress"
  );
  const completedUploads = uploads.filter((upload) => upload.status === "completed");
  const failedUploads = uploads.filter((upload) => upload.status === "failed");

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "requested":
      case "in-progress":
        return <UploadFileIcon color="primary" />;
      case "completed":
        return <CheckCircleIcon color="success" />;
      case "failed":
        return <ErrorIcon color="error" />;
      default:
        return <UploadFileIcon />;
    }
  };

  if (uploads.length === 0) {
    return null;
  }

  return (
    <StatusContainer>
      <CardContent sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: isExpanded ? 2 : 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <UploadFileIcon />
            <Typography level="title-sm">
              Uploading {uploads.length} document{uploads.length > 1 ? "s" : ""}
            </Typography>
            {pendingUploads.length > 0 && (
              <Chip size="sm" variant="soft" color="primary">
                {pendingUploads.length} pending
              </Chip>
            )}
            {completedUploads.length > 0 && (
              <Chip size="sm" variant="soft" color="success">
                {completedUploads.length} done
              </Chip>
            )}
            {failedUploads.length > 0 && (
              <Chip size="sm" variant="soft" color="danger">
                {failedUploads.length} failed
              </Chip>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton size="sm" variant="plain" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton size="sm" variant="plain" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isExpanded && (
          <UploadListContainer>
            <List size="sm">
              {uploads.map((upload) => (
                <ListItem key={upload.task_id}>
                  <ListItemDecorator>{getStatusIcon(upload.status)}</ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-sm" noWrap>
                      {upload.file?.name || upload.description}
                    </Typography>
                    {(upload.status === "requested" || upload.status === "in-progress") && (
                      <LinearProgress size="sm" variant="soft" sx={{ mt: 0.5 }} />
                    )}
                    {upload.status === "failed" && (
                      <Typography level="body-xs" color="danger">
                        Upload failed
                      </Typography>
                    )}
                    {upload.status === "completed" && (
                      <Typography level="body-xs" color="success">
                        Upload completed
                      </Typography>
                    )}
                  </ListItemContent>
                  {upload.status === "completed" && (
                    <IconButton
                      size="sm"
                      variant="plain"
                      onClick={() => onRemoveCompleted(upload.task_id)}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          </UploadListContainer>
        )}
      </CardContent>
    </StatusContainer>
  );
};
